// ===================================================================
// PrescriptionEnrollment — habilitar la cuenta para emitir recetas
// ===================================================================
//
// Antes esto mandaba al editor de perfil completo, donde la matrícula
// convivía con la dirección del consultorio y el resto de la configuración.
// Ahora es una pantalla sola que hace una cosa.
//
// No se pide el número de matrícula: se busca por DNI. REFEPS sólo se deja
// consultar por su identificador propio de 12 dígitos, que nadie se sabe de
// memoria — probado contra el registro real, buscar por la matrícula nacional
// o la provincial devuelve NOT_FOUND. Con el DNI el registro devuelve TODAS
// las matrículas de la persona, así que el profesional elige de una lista en
// vez de tipear un número que puede escribir mal.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { olvidarHabilitacionRecetas } from '../hooks/usePuedeEmitirRecetas';
import {
  ArrowLeft, ShieldCheck, Search, Loader2, Check, AlertCircle,
  BadgeCheck, Building2, CalendarDays,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TEAL = '#0FA3B1';

interface MatriculaRefeps {
  numero: string;
  tipo: string;
  emisor: string;
  habilitada: boolean;
  desde: string | null;
  especialidad: string | null;
}

const PrescriptionEnrollment: React.FC = () => {
  const navigate = useNavigate();

  const [dni, setDni] = useState('');
  const [sexo, setSexo] = useState<'' | 'F' | 'M' | 'X'>('');
  const [buscando, setBuscando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [hallazgo, setHallazgo] = useState<{
    nombreCompleto: string; dni: string; refepsId: string | null;
  } | null>(null);
  const [matriculas, setMatriculas] = useState<MatriculaRefeps[]>([]);
  const [elegida, setElegida] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  const auth = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const dniOk = dni.replace(/\D/g, '').length >= 6;

  const buscar = async () => {
    setBuscando(true); setError(''); setHallazgo(null); setMatriculas([]);
    try {
      const r = await fetch(`${API_URL}/api/prescriptions/habilitacion/buscar`, {
        method: 'POST', headers: auth(), body: JSON.stringify({ dni, sexo }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'No pudimos consultar el registro');
      if (!j.encontrado) { setError(j.mensaje); return; }

      setHallazgo(j.profesional);
      setMatriculas(j.matriculas || []);
      // Si tiene una sola, queda elegida: no tiene sentido hacerlo elegir.
      if ((j.matriculas || []).length === 1) setElegida(j.matriculas[0].numero);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido');
    } finally { setBuscando(false); }
  };

  const confirmar = async () => {
    if (!elegida) return;
    setGuardando(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/prescriptions/habilitacion`, {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ dni, sexo, matricula: elegida }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'No pudimos guardar la matrícula');
      // El hook que gobierna los botones "Crear receta" del calendario cachea
      // la habilitación mientras dura la sesión. Sin esto, el profesional
      // acaba de cargar su matrícula y los botones siguen sin aparecer hasta
      // que recargue la página entera.
      olvidarHabilitacionRecetas();
      setListo(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido');
    } finally { setGuardando(false); }
  };

  // -----------------------------------------------------------------
  // Confirmado
  // -----------------------------------------------------------------
  if (listo) {
    const m = matriculas.find((x) => x.numero === elegida);
    return (
      <div className="min-h-screen bg-[#F2F2F7] px-4">
        <div className="max-w-lg mx-auto pt-16 text-center animate-[aparecer_.4s_ease-out]">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5 animate-[latir_.5s_ease-out]">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-[28px] font-bold text-gray-900">Ya podés emitir recetas</h1>
          <p className="text-[15px] text-gray-700 mt-2">
            Tu matrícula quedó validada contra el Registro Federal de Profesionales de la Salud.
          </p>

          <div className="bg-white rounded-2xl p-4 mt-6 text-left">
            <p className="text-[12px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
              Matrícula registrada
            </p>
            <p className="text-[17px] font-bold text-gray-900">{elegida}</p>
            {m && <p className="text-[13px] text-gray-700">{m.emisor}</p>}
            {hallazgo && (
              <p className="text-[13px] text-gray-500 mt-2 pt-2 border-t border-gray-100">
                {hallazgo.nombreCompleto}
              </p>
            )}
          </div>

          <button
            onClick={() => navigate('/professional/prescriptions')}
            className="mt-6 w-full py-3.5 rounded-2xl text-white font-semibold text-[16px] active:scale-[0.98] transition-transform"
            style={{ backgroundColor: TEAL }}
          >
            Ir a Recetas
          </button>
        </div>

        <style>{ANIMACIONES}</style>
      </div>
    );
  }

  // -----------------------------------------------------------------
  // Buscar y elegir
  // -----------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#F2F2F7] px-4 pb-12 text-left">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[15px] pt-5 mb-4 -ml-1 active:opacity-60"
          style={{ color: TEAL }}
        >
          <ArrowLeft className="w-5 h-5" />
          Volver
        </button>

        <div className="flex items-center gap-3 mb-1">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${TEAL}1A` }}
          >
            <ShieldCheck className="w-6 h-6" style={{ color: TEAL }} />
          </div>
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight">
            Habilitar recetas
          </h1>
        </div>
        <p className="text-[15px] text-gray-700 mb-6">
          Validamos tu matrícula contra el Registro Federal de Profesionales de la Salud.
          Buscamos por DNI: no hace falta que sepas tu número de registro.
        </p>

        {/* Paso 1 — identidad */}
        <div className="bg-white rounded-2xl p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">
                DNI <span className="text-red-400">*</span>
              </label>
              <input
                inputMode="numeric"
                className="w-full px-3 py-2.5 text-[15px] text-gray-900 border border-gray-300 rounded-xl placeholder:text-gray-500 outline-none focus:ring-2 transition"
                style={{ ['--tw-ring-color' as any]: TEAL }}
                value={dni}
                onChange={(e) => { setDni(e.target.value); setHallazgo(null); setMatriculas([]); }}
                placeholder="Sin puntos"
              />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">
                Sexo <span className="text-red-400">*</span>
              </label>
              <select
                className="w-full px-3 py-2.5 text-[15px] text-gray-900 border border-gray-300 rounded-xl outline-none focus:ring-2 transition"
                style={{ ['--tw-ring-color' as any]: TEAL }}
                value={sexo}
                onChange={(e) => { setSexo(e.target.value as any); setHallazgo(null); setMatriculas([]); }}
              >
                <option value="">Elegir…</option>
                <option value="F">Femenino</option>
                <option value="M">Masculino</option>
                <option value="X">X</option>
              </select>
            </div>
          </div>
          <p className="text-[12px] text-gray-500">
            El registro necesita los dos datos para encontrarte. Tienen que coincidir con tu documento.
          </p>

          <button
            onClick={buscar}
            disabled={!dniOk || !sexo || buscando}
            className="w-full py-3 rounded-xl text-white font-semibold text-[15px] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-all"
            style={{ backgroundColor: TEAL }}
          >
            {buscando
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Consultando el registro…</>
              : <><Search className="w-4 h-4" /> Buscarme en el registro</>}
          </button>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 animate-[aparecer_.3s_ease-out]">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Paso 2 — elegir la matrícula */}
        {hallazgo && (
          <div className="mt-5 animate-[subir_.35s_ease-out]">
            <div className="bg-white rounded-2xl p-4 mb-3">
              <div className="flex items-start gap-2.5">
                <BadgeCheck className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: TEAL }} />
                <div className="min-w-0">
                  <p className="text-[12px] uppercase tracking-wide text-gray-500 font-semibold">
                    Te encontramos
                  </p>
                  <p className="text-[17px] font-bold text-gray-900 mt-0.5">
                    {hallazgo.nombreCompleto}
                  </p>
                  <p className="text-[13px] text-gray-700">DNI {hallazgo.dni}</p>
                </div>
              </div>
            </div>

            <p className="text-[13px] font-semibold text-gray-800 mb-2 px-1">
              {matriculas.length === 1
                ? 'Tu matrícula'
                : `Tenés ${matriculas.length} matrículas. Elegí con cuál vas a recetar:`}
            </p>

            {/* La provincia importa y no es obvio.
                Al emitir con la matrícula nacional teniendo consultorio en
                Corrientes, Qbi2 devuelve "PARA PRESCRIBIR DEBE TENER MATRICULA
                PROVINCIAL DE CORRIENTES". El profesional se enteraba recién al
                firmar, y con la receta ya cargada. */}
            {matriculas.length > 1 && (
              <div className="mb-3 px-3 py-2.5 rounded-xl bg-[#FF9500]/10 border border-[#FF9500]/30">
                <p className="text-[13px] text-[#8A4B00] leading-snug">
                  Elegí la <span className="font-semibold">matrícula de la provincia donde atendés</span>.
                  Varias obras sociales rechazan las recetas hechas con la matrícula
                  nacional si el consultorio está en una provincia donde también
                  tenés matrícula propia.
                </p>
              </div>
            )}

            <div className="space-y-2">
              {matriculas.map((m, i) => {
                const activa = elegida === m.numero;
                return (
                  <button
                    key={m.numero}
                    onClick={() => m.habilitada && setElegida(m.numero)}
                    disabled={!m.habilitada}
                    style={{
                      animationDelay: `${i * 70}ms`,
                      borderColor: activa ? TEAL : undefined,
                      backgroundColor: activa ? `${TEAL}0F` : undefined,
                    }}
                    className={`w-full text-left rounded-2xl p-4 border-2 transition-all animate-[subir_.3s_ease-out_both] ${
                      activa ? '' : 'bg-white border-transparent'
                    } ${m.habilitada ? 'active:scale-[0.99]' : 'opacity-50 cursor-not-allowed'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[17px] font-bold text-gray-900">{m.numero}</p>
                        <p className="text-[13px] text-gray-700 flex items-center gap-1.5 mt-0.5">
                          <Building2 className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" />
                          {m.emisor}
                        </p>
                        {m.desde && (
                          <p className="text-[12px] text-gray-500 flex items-center gap-1.5 mt-0.5">
                            <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" />
                            Desde {new Date(m.desde).toLocaleDateString('es-AR')}
                          </p>
                        )}
                        {!m.habilitada && (
                          <p className="text-[12px] text-red-600 font-semibold mt-1">
                            Figura como no habilitada
                          </p>
                        )}
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                          activa ? '' : 'border-2 border-gray-300'
                        }`}
                        style={activa ? { backgroundColor: TEAL } : undefined}
                      >
                        {activa && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={confirmar}
              disabled={!elegida || guardando}
              className="mt-5 w-full py-3.5 rounded-2xl text-white font-semibold text-[16px] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-all"
              style={{ backgroundColor: TEAL }}
            >
              {guardando
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Validando…</>
                : <><ShieldCheck className="w-5 h-5" /> Confirmar y habilitar</>}
            </button>
          </div>
        )}
      </div>

      <style>{ANIMACIONES}</style>
    </div>
  );
};

// Las animaciones van inline y no en el CSS global: son de esta pantalla y
// no conviene sumar reglas globales a una hoja que ya arrastra cosas de la
// plantilla original.
const ANIMACIONES = `
  @keyframes aparecer { from { opacity: 0 } to { opacity: 1 } }
  @keyframes subir    { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: none } }
  @keyframes latir    { 0% { transform: scale(.6); opacity: 0 } 60% { transform: scale(1.08) } 100% { transform: scale(1); opacity: 1 } }
  @media (prefers-reduced-motion: reduce) {
    [class*="animate-["] { animation: none !important }
  }
`;

export default PrescriptionEnrollment;
